/*
 * New PDP — Convermax verify-fitment: relocate + pop-up form.
 *
 * Convermax injects #cm-verify-fitment into a buy-box app block and renders the Year/Make/Model
 * selection form directly (it has no collapsed state of its own). We:
 *
 * 1) Relocate the widget — and a theme-built "Verify" CTA — into the placement mount
 *    ([data-npd-fitment-mount]) or the details column, and hide the emptied app block.
 *
 * 2) Gate on the same runtime signals the live PDP uses:
 *      - .cm_verify-fitment__universal / __unknown, a stored vehicle, or a fit result
 *        => show the Convermax widget inline (universal block / result chip).
 *      - .cm_vehicle-widget_dropdowns with no vehicle yet
 *        => show the theme CTA inline and keep the raw form out of the page flow.
 *    Universal-fit products therefore never show the CTA (nothing to select).
 *
 * 3) Open only the empty selection form in a pop-up: the CTA moves the widget into the New PDP
 *    fitment <modal-dialog> and opens it. Selecting a vehicle (Convermax.onVehicleSelected) or
 *    dismissing the modal returns the widget inline (a dismiss resets it via the Convermax API).
 *
 * All visual styling lives in section-new-pdp.css. Scoped entirely to the New PDP A/B template.
 */
(function () {
  var mount = document.querySelector('[data-npd-fitment-mount]');
  var target = mount || document.querySelector('.new-pdp-details__fb-side');
  if (!target) return;
  var placement = mount ? mount.getAttribute('data-npd-fitment-mount') : 'details';

  var modal = document.querySelector('[data-npd-fitment-modal]');
  var modalBody = modal && modal.querySelector('[data-npd-fitment-modal-body]');
  var productId = modal && modal.getAttribute('data-product-id');
  var convermaxBound = false;

  function widget() {
    return document.getElementById('cm-verify-fitment');
  }
  function cta() {
    return document.querySelector('[data-npd-fitment-cta]');
  }
  function inModal() {
    var w = widget();
    return !!(w && modalBody && w.parentElement === modalBody);
  }

  function moveWidgetInline() {
    var w = widget();
    if (w && w.parentElement !== target) target.appendChild(w);
  }

  function relocate() {
    var w = widget();
    if (!w) return false;
    var c = cta();
    if (c && c.parentElement !== target) target.appendChild(c);
    if (w.parentElement !== target && !inModal()) {
      var appBlock = w.closest('.shopify-app-block');
      w.classList.add('new-pdp-fitment-verify');
      if (placement === 'after_title') w.classList.add('new-pdp-fitment-verify--chip');
      target.appendChild(w);
      // The app block only hosted the fitment widget — hide the now-hollow shell.
      if (appBlock) appBlock.style.display = 'none';
    }
    return true;
  }

  function fitmentState() {
    var w = widget();
    if (!w) return null;
    return {
      universal: !!w.querySelector('.cm_verify-fitment__universal'),
      unknown: !!w.querySelector('.cm_verify-fitment__unknown'),
      result: !!w.querySelector('.cm_verify-fitment__yes, .cm_verify-fitment__no'),
      hasForm: !!w.querySelector('.cm_vehicle-widget_dropdowns'),
      hasVehicle: !!localStorage.getItem('cm_variant-model')
    };
  }

  // Show the Convermax widget inline for universal / result states; otherwise show the CTA when a
  // selectable form exists. Skipped while the form is open in the modal.
  function applyState() {
    if (inModal()) return;
    var w = widget();
    var c = cta();
    if (!w || !c) return;
    var s = fitmentState();
    if (!s) return;
    var showWidget = s.universal || s.unknown || s.result || s.hasVehicle;
    var showCta = !showWidget && s.hasForm;
    w.style.display = showWidget ? 'block' : 'none';
    c.style.display = showCta ? 'flex' : 'none';
  }

  function openFormModal() {
    if (!modal || !modalBody) return;
    var w = widget();
    if (!w) return;
    if (w.parentElement !== modalBody) modalBody.appendChild(w);
    w.style.display = 'block';
    if (typeof modal.show === 'function') modal.show();
    else modal.setAttribute('open', '');
  }

  function bindConvermax() {
    if (convermaxBound || !window.Convermax) return convermaxBound;
    var prev = window.Convermax.onVehicleSelected;
    window.Convermax.onVehicleSelected = function (vehicle) {
      if (typeof prev === 'function') {
        try {
          prev(vehicle);
        } catch (e) {
          /* keep our handler running even if a prior subscriber throws */
        }
      }
      moveWidgetInline();
      if (modal && modal.hasAttribute('open')) {
        if (typeof modal.hide === 'function') modal.hide();
        else modal.removeAttribute('open');
      }
      applyState();
    };
    convermaxBound = true;
    return true;
  }

  // The CTA (empty form) and the "Change vehicle" link both open the selection form in the pop-up.
  // Capture phase so we act before Convermax does.
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target.closest) return;
      var fromCta = e.target.closest('[data-npd-fitment-cta]');
      var fromChange = e.target.closest('.cm_verify-fitment_change');
      if (!fromCta && !fromChange) return;
      e.preventDefault();
      if (fromChange) {
        // Convermax's own "change" switches the widget to a sticky, pre-filled form mode that
        // can't be reverted to the result view (and auto-re-commits the vehicle). Block it and
        // start a fresh selection instead, so the pop-up mirrors the initial verify exactly.
        e.stopImmediatePropagation();
        if (window.Convermax && typeof window.Convermax.discardVehicle === 'function') {
          try {
            window.Convermax.discardVehicle();
          } catch (ex) {
            /* fall through and open whatever the widget currently shows */
          }
        }
      }
      openFormModal();
    },
    true
  );

  // Dismissed without selecting a vehicle → return the widget inline and reset it to the prompt.
  if (modal) {
    // ModalDialog.hide() fires `modalClosed` before it clears the `open` attribute, so we can't
    // gate on that — instead treat "our widget is still in the modal" as the dismiss signal.
    document.body.addEventListener('modalClosed', function () {
      if (!inModal()) return;
      // The pop-up always opens a fresh, no-vehicle form (the CTA path, and the "change" path after
      // discarding), so a dismiss means the shopper backed out without choosing — return the widget
      // inline and reset it to the "Verify" prompt (CTA).
      moveWidgetInline();
      if (window.Convermax && typeof window.Convermax.updateVerifyFitmentWidget === 'function' && productId) {
        try {
          window.Convermax.updateVerifyFitmentWidget(productId);
        } catch (e) {
          /* Convermax not ready — the widget keeps its current inline render. */
        }
      }
      applyState();
    });
  }

  function observe() {
    var frame = null;
    var mo = new MutationObserver(function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        bindConvermax();
        applyState();
      });
    });
    mo.observe(target, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    window.addEventListener('storage', function (e) {
      if (e.key === 'cm_variant-model') applyState();
    });
  }

  function start() {
    bindConvermax();
    applyState();
    observe();
  }

  if (relocate()) {
    start();
    return;
  }

  var initObserver = new MutationObserver(function () {
    if (relocate()) {
      initObserver.disconnect();
      start();
    }
  });
  initObserver.observe(document.body, { childList: true, subtree: true });
  setTimeout(function () {
    initObserver.disconnect();
  }, 15000);
})();
